import * as React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ConfiguratorPlayer } from '../components/configurator-player';
import { ThemeProvider } from '../components/theme-provider';
import { configurePlayerApiBase } from '../lib/api-base';

export interface WidgetMountOptions {
  product: string;
  server: string;
  hideControls?: boolean;
  hideConfigurator?: boolean;
  hideUi?: boolean;
  hideToolbar?: boolean;
  cleanView?: boolean;
  textureQualityPreset?: string | null;
  perfPreset?: string | null;
  /**
   * Selections to apply at session init, e.g. resolved from a share token by the
   * host page. Read once when the session is created, so the host must have them
   * in hand before calling mount.
   */
  initialSelections?: Record<string, unknown> | null;
}

export interface WidgetMountHandle {
  destroy: () => void;
}

export function mount(container: HTMLElement, options: WidgetMountOptions): WidgetMountHandle {
  const server = (options.server || '').replace(/\/+$/, '') || window.location.origin;
  configurePlayerApiBase(server);

  const root: Root = createRoot(container);
  root.render(
    <>
      <ThemeProvider metadata={undefined} />
      <div className="font-sans antialiased h-full min-h-[320px] w-full">
        <ConfiguratorPlayer
          productId={options.product}
          initialSelections={options.initialSelections ?? undefined}
          embed
          hideControls={options.hideControls}
          hideConfigurator={options.hideConfigurator}
          hideUi={options.hideUi}
          hideToolbar={options.hideToolbar}
          cleanView={options.cleanView}
          textureQualityPreset={options.textureQualityPreset ?? null}
          perfPreset={options.perfPreset ?? null}
        />
      </div>
    </>,
  );

  return {
    destroy() {
      root.unmount();
    },
  };
}

declare global {
  interface Window {
    Config3DWidget?: { mount: typeof mount; version: string };
  }
}

window.Config3DWidget = { mount, version: '1.0.2' };
